import moment from 'moment';
import { useCallback, useState, useEffect } from 'react';
import { sendCloudwatchRUMError } from '$src/utils/cloudwatchRUM';
import useStores from './useStores';

const NUMBER_OF_WEEKS = 8;

const INITIAL_CHART_STATE = {
  count: 0,
  labels: [],
  points: [],
};

export const weeksBackwards = Array.from({ length: NUMBER_OF_WEEKS }, (_, index) => {
  const firstDayOfWeek = moment().startOf('week');
  const date = moment(firstDayOfWeek)
    .subtract(NUMBER_OF_WEEKS - 1, 'weeks')
    .add(index, 'weeks');

  return date;
});

const generateDataPoints = (timeseries) => {
  const dataPointsPerWeek = Array(NUMBER_OF_WEEKS).fill(0);

  timeseries?.items?.[0].timeseries?.forEach((point) => {
    const dataPointWeek = moment.unix(point.startTime).week();
    const indexWeek = weeksBackwards.findIndex((date) => moment(date, 'MMM-DD').week() === dataPointWeek);

    if (indexWeek >= 0) {
      dataPointsPerWeek[indexWeek] += point.count;
    }
  });

  return dataPointsPerWeek;
};

export default function useEventsChartData(userIds, locationId) {
  const {
    eventStore: { fetchEventsTimeseries, fetchEmergenciesTimeseries },
  } = useStores();
  const [loadingEventsChartData, setLoadingEventsChartData] = useState(false);
  const [loadingEmergencyChartData, setLoadingEmergencyChartData] = useState(false);
  const [eventsChartData, setEventsChartData] = useState(INITIAL_CHART_STATE);
  const [emergenciesChartData, setEmergenciesChartData] = useState(INITIAL_CHART_STATE);

    // Validate input parameters
    const isValidRequest = useCallback(() => {  
      if (!Array.isArray(userIds)) {
        console.error('userIds must be an array');
        return false;
      }
  
      if (userIds.length === 0) {
        console.error('At least one user ID is required for fetching events data');
        return false;
      }
  
      if (userIds.some(id => !id)) {
        console.error('All user IDs must be valid');
        return false;
      }

      if (locationId && userIds.length > 1) {
        console.error('Location-specific queries support only one user ID. Received multiple user IDs');
        return false;
      }
  
      return true;
    }, [locationId, userIds]);

  useEffect(() => {
    const getEventsTimeseries = async () => {
      if (!loadingEventsChartData) {
        if (!isValidRequest()) {
          setEventsChartData(INITIAL_CHART_STATE);
          return;
        }

        try {
          setLoadingEventsChartData(true);

          const timeseries = await Promise.all(userIds.map((userId) => fetchEventsTimeseries(userId, locationId)));
          const allDataPoints = timeseries?.map((t) => generateDataPoints(t));

          const sumOfPoints = allDataPoints.reduce((acc, currentArray) => acc.map((sum, index) => sum + (currentArray[index] || 0)), new Array(NUMBER_OF_WEEKS).fill(0));
          
          setEventsChartData({
            count: timeseries?.reduce((acc, curr) => acc + (curr?.count || 0), 0) ?? 0,
            labels: weeksBackwards,
            points: sumOfPoints,
          });
        } catch (error) {
          sendCloudwatchRUMError(`There was an error fetching Events timeseries`, error);
        } finally {
          setLoadingEventsChartData(false);
        }
      }
    };

    const getEmergenciesTimeseries = async () => {
      if (!loadingEmergencyChartData) {
        if (!isValidRequest()) {
          setEmergenciesChartData(INITIAL_CHART_STATE);
          return;
        }

        try {
          setLoadingEmergencyChartData(true);

          const timeseries = await Promise.all(userIds.map((userId) => fetchEmergenciesTimeseries(userId, locationId)));
          const allDataPoints = timeseries?.map((t) => generateDataPoints(t));

          const sumOfPoints = allDataPoints.reduce((acc, currentArray) => acc.map((sum, index) => sum + (currentArray[index] || 0)), new Array(NUMBER_OF_WEEKS).fill(0));

          setEmergenciesChartData({
            count: timeseries?.reduce((acc, curr) => acc + (curr?.count || 0), 0) ?? 0,
            labels: weeksBackwards,
            points: sumOfPoints,
          });
        } catch (error) {
          sendCloudwatchRUMError(`There was an error fetching Emergencies timeseries`, error);
        } finally {
          setLoadingEmergencyChartData(false);
        }
      }
    };

    getEventsTimeseries();
    getEmergenciesTimeseries();
  }, [userIds, fetchEventsTimeseries, fetchEmergenciesTimeseries, locationId]);

  return {
    isLoadingEventsChartData: loadingEventsChartData,
    isLoadingEmergencyChartData: loadingEmergencyChartData,
    eventsChartData,
    emergenciesChartData,
  };
}
